import React,{createContext,useContext,useEffect,useRef,useState} from 'react'
import {supabase} from '../lib/supabase'
import {mapLegacyRole} from '../utils/roles'

const AUTH_FORCE_PROFILE_EVENTS=new Set(['SIGNED_IN','SIGNED_OUT','USER_UPDATED'])

const AuthContext=createContext({})

export const useAuth=()=> {
  return useContext(AuthContext)
}

export const AuthProvider=({children})=> {
  const isDev=Boolean(import.meta.env?.DEV)
  const [user,setUser]=useState(null)
  const [loading,setLoading]=useState(true)
  const [professionalProfile,setProfessionalProfile]=useState(null)
  const [profileLoading,setProfileLoading]=useState(false)
  const [profileError,setProfileError]=useState(null)
  const lastUserIdRef=useRef(null)
  const professionalProfileRef=useRef(null)
  const profileLoadInProgressRef=useRef(false)
  const hasInitializedRef=useRef(false)
  const [lastAuthEvent,setLastAuthEvent]=useState(null)
  const userId=user?.id ?? null
  const professionalProfileId=professionalProfile?.id ?? null

  useEffect(()=> {
    if (hasInitializedRef.current) {
      return
    }

    hasInitializedRef.current=true
    // Get initial session
    supabase.auth.getSession().then(({data: {session}})=> {
      const sessionUser=session?.user ?? null
      const sessionUserId=sessionUser?.id ?? null

      setUser(sessionUser)

      if (sessionUserId) {
        lastUserIdRef.current=sessionUserId
        loadProfessionalProfile(sessionUserId)
      } else {
        lastUserIdRef.current=null
        setProfessionalProfile(null)
        setProfileLoading(false)
        setProfileError(null)
      }
      setLoading(false)
    })

    // Listen for auth changes
    const {data: {subscription}}=supabase.auth.onAuthStateChange(async (_event,session)=> {
      const sessionUser=session?.user ?? null
      const sessionUserId=sessionUser?.id ?? null
      const isTokenRefresh=_event==='TOKEN_REFRESHED'
      const isSameUser=sessionUserId && lastUserIdRef.current===sessionUserId
      const shouldUpdateUser=!isTokenRefresh || !isSameUser

      setLastAuthEvent(_event)
      if (isDev) {
        console.log(`[${new Date().toISOString()}] onAuthStateChange`,{
          event: _event,
          sessionUserId: sessionUserId ?? null
        })
      }

      if (shouldUpdateUser) {
        setUser(sessionUser)
      }

      if (sessionUserId) {
        const isFirstLoad=lastUserIdRef.current===null
        const shouldForceProfileLoad=
          AUTH_FORCE_PROFILE_EVENTS.has(_event) || isFirstLoad

        lastUserIdRef.current=sessionUserId
        const isCachedProfileForUser=
          professionalProfileRef.current &&
          (professionalProfileRef.current.auth_id===sessionUserId ||
            professionalProfileRef.current.user_id===sessionUserId)
        const shouldSkipProfileLoad=
          (_event==='SIGNED_IN' || _event==='USER_UPDATED') &&
          isCachedProfileForUser

        if (!shouldSkipProfileLoad && !(_event==='INITIAL_SESSION' && profileLoadInProgressRef.current)) {
          loadProfessionalProfile(sessionUserId,shouldForceProfileLoad)
        }
      } else {
        lastUserIdRef.current=null
        setProfessionalProfile(null)
        setProfileLoading(false)
        setProfileError(null)
      }
      setLoading(false)
    })

    return ()=> {
      hasInitializedRef.current=false
      subscription.unsubscribe()
    }
  },[])

  useEffect(()=> {
    if (!isDev) {
      return
    }

    console.log(`[${new Date().toISOString()}] AuthContext state`,{
      loading,
      profileLoading,
      userId,
      professionalProfileId,
      lastAuthEvent
    })
  },[isDev,loading,profileLoading,userId,professionalProfileId,lastAuthEvent])

  useEffect(()=> {
    professionalProfileRef.current=professionalProfile
  },[professionalProfile])

  const normalizeProfessionalProfile=(profile)=> {
    if (!profile) {
      return profile
    }

    const normalizedRole=mapLegacyRole(profile.role)

    if (normalizedRole && normalizedRole!==profile.role) {
      return {
        ...profile,
        role: normalizedRole
      }
    }

    return profile
  }

  const loadProfessionalProfile=async (userId,force=false)=> {
    if (!userId) {
      lastUserIdRef.current=null
      setProfessionalProfile(null)
      setProfileLoading(false)
      return
    }

    if (!force && lastUserIdRef.current===userId && profileLoadInProgressRef.current) {
      return
    }

    if (!force && lastUserIdRef.current===userId && professionalProfileRef.current) {
      return
    }

    lastUserIdRef.current=userId
    setProfileLoading(true)
    setProfileError(null)
    profileLoadInProgressRef.current=true
    try {
      // Try with auth_id first (preferred method)
      let {data,error,status}=await supabase
        .from('users_pf')
        .select('*')
        .eq('auth_id',userId)
        .single()

      // If auth_id doesn't work, try with user_id (fallback)
      if (error && error.code==='PGRST116') {
        const {data: fallbackData,error: fallbackError,status: fallbackStatus}=await supabase
          .from('users_pf')
          .select('*')
          .eq('user_id',userId)
          .single()

        if (!fallbackError) {
          data=fallbackData
          error=null
          status=fallbackStatus
        } else {
          error=fallbackError
          status=fallbackStatus
        }
      }

      if (error && error.code !=='PGRST116') {
        const statusCode=error?.status ?? status
        const permissionDenied=
          statusCode===401 ||
          statusCode===403 ||
          error?.code==='42501' ||
          (error?.message && error.message.toLowerCase().includes('permission'))
        const requiresLogout=statusCode===401 || statusCode===403

        console.error('Error loading professional profile:',error)

        if (permissionDenied) {
          const message=requiresLogout
            ? 'Your session appears to be invalid. Please sign in again to continue.'
            : 'We could not access your profile due to permissions. Please contact support to restore access, then try again.'

          setProfileError({
            type: 'permission',
            message,
            canRetry: !requiresLogout,
            requiresLogout
          })
          setProfessionalProfile(null)

          if (requiresLogout) {
            await supabase.auth.signOut()
          }
        } else {
          setProfileError({
            type: 'unknown',
            message: 'We were unable to load your profile. Please try again or contact support if the issue persists.',
            canRetry: true,
            requiresLogout: false
          })
        }
      } else if (data) {
        setProfessionalProfile(normalizeProfessionalProfile(data))
        setProfileError(null)
      } else {
        setProfessionalProfile(null)
        setProfileError({
          type: 'missing',
          message: 'We could not find your profile record. Please contact support to create or restore it, then retry.',
          canRetry: true,
          requiresLogout: false
        })
      }
    } catch (error) {
      console.error('Error loading professional profile:',error)
      setProfileError({
        type: 'unknown',
        message: 'Something went wrong while loading your profile. Please try again or contact support.',
        canRetry: true,
        requiresLogout: false
      })
    } finally {
      setProfileLoading(false)
      profileLoadInProgressRef.current=false
    }
  }

  const signIn=async (email,password)=> {
    const {data,error}=await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return {data,error}
  }

  const signUp=async (email,password,userData)=> {
    const {data,error}=await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })

    // If signup is successful,create profile in users_pf table
    if (!error && data.user) {
      const {error: profileError}=await supabase
        .from('users_pf')
        .insert({
          auth_id: data.user.id, // Use auth_id consistently
          user_id: data.user.id,
          email: email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          phone: userData.phone || '',
          city: userData.city || '',
          state: userData.state || '',
          role: 'client', // Default role
          status: 'active',
          created_at: new Date().toISOString()
          // Note: Don't manually set 'name' - database trigger will handle it
        })

      if (profileError) {
        console.error('Error creating user profile:',profileError)
      }
    }

    return {data,error}
  }

  const signOut=async ()=> {
    setProfessionalProfile(null)
    setProfileError(null)
    setProfileLoading(false)
    const {error}=await supabase.auth.signOut()
    return {error}
  }

  const updateProfile=async (updates)=> {
    if (!user) return {error: new Error('No user logged in')}

    // Try to update with auth_id first
    let {data,error}=await supabase
      .from('users_pf')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('auth_id',user.id)
      .select()
      .single()

    // If auth_id doesn't work, try with user_id (fallback)
    if (error && error.code==='PGRST116') {
      const {data: fallbackData,error: fallbackError}=await supabase
        .from('users_pf')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id',user.id)
        .select()
        .single()

      if (!fallbackError) {
        data=fallbackData
        error=null
      }
    }

    if (!error && data) {
      setProfessionalProfile(normalizeProfessionalProfile(data))
    }

    return {data,error}
  }

  const value={
    user,
    professionalProfile,
    profileId: professionalProfile?.id ?? null,
    signIn,
    signUp,
    signOut,
    updateProfile,
    loading,
    profileLoading,
    profileError,
    reloadProfessionalProfile: (force=false)=> user?.id ? loadProfessionalProfile(user.id,force) : null
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
